import type { Attach } from '@app/shared/_models/common';
import { SALES_ORDER_MODE_ASAP_DELIVERY, sales_order_mode } from '@app/shared/constants';
import { isPrescriptionFile, prescriptionRefsOf } from './rental-billing.service';

// Pièces jointes d'un dossier de location rangées par matériel, lues dans GET /sales/order/:id?o=deliveries,attachs[,open].
// Un matériel a les siennes (pièces du dossier marquées data.rental_lines), ses ordonnances, et celles des BL qui le livrent (+) ou le reprennent (−1 sur sa ligne).

export interface BlDocs extends DeliveryDocs { kind: 'delivery' | 'return'; }
export interface LineDocs { own: Attach[]; rx: Attach[]; bls: BlDocs[]; }

// mode, askdatdel, askslot : lien vers le board des tournées ; signedPdf : PDF d'archivage rangé à la signature (GET /deliveries/:id/signed-bl).
export interface DeliveryDocs { id: number; realdatdel: string | null; mode: number; askdatdel: string | null; askslot: number | null; signedPdf: boolean; docs: Attach[]; }

// Même repère que le serveur (get_signed_bl_pdf) : pièce active marquée data.flags « signed_bl » ; un BL réalisé avant l'archivage n'en a pas.
export function hasSignedBlPdf(list: unknown): boolean {
  return (Array.isArray(list) ? list : []).some((a: any) => a?.is_active !== false && Array.isArray(a?.data?.flags) && a.data.flags.includes('signed_bl'));
}

// Infobulles des liens grisés d'un BL (carte « BL du dossier », survol d'un patient du Planning).
export function signedOffTip(realdatdel: unknown): string {
  return realdatdel ? "BL signé avant l'archivage des PDF : aucun BL signé à ouvrir" : 'BL pas encore signé';
}
export function boardOffTip(mode: unknown, askdatdel: unknown): string {
  const m = Number(mode) || 0;
  return m < SALES_ORDER_MODE_ASAP_DELIVERY ? `${sales_order_mode.get(m) ?? 'Ce mode de livraison'} : pas de tournée` : !askdatdel ? 'Pas de jour demandé : pas de tournée' : '';
}

// Index par id de ligne, construits une fois par lecture : un établissement a des milliers de lignes et de BL.
export interface DossierDocs { own: Map<number, Attach[]>; rx: Map<number, Attach[]>; bls: Map<number, { bl: DeliveryDocs; back: boolean }[]>; }

export const NO_DOCS: DossierDocs = { own: new Map(), rx: new Map(), bls: new Map() };

// data.rental_lines : ids des matériels auxquels la pièce a été jointe (trombone du patient ou fenêtre d'un matériel).
export function lineRefsOf(a: Attach | null | undefined): number[] {
  const v = a?.data?.['rental_lines'];
  return (Array.isArray(v) ? v : []).map(Number).filter((n) => Number.isInteger(n) && n > 0);
}

// Pièce montrée sur ses matériels, pas dans les documents du dossier lui-même.
export function isLineFile(a: Attach): boolean {
  return isPrescriptionFile(a) || lineRefsOf(a).length > 0;
}

// Comme quotationdocs : le mémo (is_default_memo) n'est pas un document ; tri par rang puis id.
function usable(list: unknown): Attach[] {
  return (Array.isArray(list) ? list : [])
    .filter((a: any) => +a?.id > 0 && !(Array.isArray(a?.data?.flags) && a.data.flags.includes('is_default_memo')))
    .sort((x: any, y: any) => (x.seq ?? Number.MAX_SAFE_INTEGER) - (y.seq ?? Number.MAX_SAFE_INTEGER) || +x.id - +y.id);
}

function push<T>(m: Map<number, T[]>, k: number, v: T): void {
  const list = m.get(k);
  if (list) { if (!list.includes(v)) list.push(v); } else m.set(k, [v]);
}

export function dossierDocsOf(order: any): DossierDocs {
  const d: DossierDocs = { own: new Map(), rx: new Map(), bls: new Map() };
  for (const a of usable(order?.lnk_attachs)) {
    for (const id of lineRefsOf(a)) push(d.own, id, a);
    for (const r of prescriptionRefsOf(a)) push(d.rx, r.line_id, a);
  }
  for (const bl of Array.isArray(order?.lnk_deliveries) ? order.lnk_deliveries : []) {
    if (!(Number(bl?.mode) >= 0) || !(+bl?.id > 0)) continue;   // un BL annulé a un mode négatif
    const entry: DeliveryDocs = {
      id: +bl.id, realdatdel: bl.realdatdel ?? null, mode: Number(bl.mode) || 0, askdatdel: bl.askdatdel ?? null, askslot: bl.askslot ?? null,
      signedPdf: !!bl.realdatdel && hasSignedBlPdf(bl.lnk_attachs), docs: usable(bl.lnk_attachs),
    };
    for (const dl of bl.lnk_deliveryl ?? []) if (Number(dl?.qty)) push(d.bls, Number(dl.id_order_l), { bl: entry, back: Number(dl.qty) < 0 });
  }
  return d;
}

// Tableaux neufs : le carrousel les modifie en place (suppression, rang), l'index reste celui de la lecture.
export function lineDocsOf(d: DossierDocs, lineId: number): LineDocs {
  const bls: BlDocs[] = (d.bls.get(lineId) ?? []).map(({ bl, back }) => ({ ...bl, kind: back ? 'return' as const : 'delivery' as const, docs: [...bl.docs] }));
  return { own: [...(d.own.get(lineId) ?? [])], rx: [...(d.rx.get(lineId) ?? [])], bls };
}

// Pièces distinctes d'un ensemble de matériels (une pièce jointe au patient compte une fois pour ses deux matériels).
export function docsCountOf(d: DossierDocs, lines: { id: number }[]): number {
  const ids = new Set<number>();
  for (const l of lines) {
    for (const a of d.own.get(l.id) ?? []) ids.add(+a.id!);
    for (const a of d.rx.get(l.id) ?? []) ids.add(+a.id!);
    for (const { bl } of d.bls.get(l.id) ?? []) for (const a of bl.docs) ids.add(+a.id!);
  }
  return ids.size;
}
